import { useMutation } from '@apollo/client';
import { message } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    EXECUTE_CONSOLIDATION_ASYNC,
    ExecuteConsolidationAsyncResponse,
    ExecuteConsolidationAsyncVariables
} from '../graphql/mutations/executeConsolidationAsync';
import { GET_BAG_TASK_LOG, GetBagTaskLogResponse } from '../graphql/queries/getBagTaskLog';
import { useBagTaskLogPolling } from './useBagTaskLogPolling';

interface UseExecuteConsolidationAsyncOptions {
    onSuccess?: (data: GetBagTaskLogResponse['getBagTaskLog']) => void;
    onError?: (error: string) => void;
    pollingInterval?: number;
    maxPollingTime?: number;
}

interface UseExecuteConsolidationAsyncResult {
    executeAsync: (variables: ExecuteConsolidationAsyncVariables) => Promise<void>;
    loading: boolean;
    isExecuting: boolean; // 是否正在执行（包括轮询阶段）
    currentTaskLog: GetBagTaskLogResponse['getBagTaskLog'] | null;
    error: string | null;
}

export const useExecuteConsolidationAsync = (
    options: UseExecuteConsolidationAsyncOptions = {}
): UseExecuteConsolidationAsyncResult => {
    const { t } = useTranslation();
    const { onSuccess, onError, pollingInterval, maxPollingTime } = options;
    const [isExecuting, setIsExecuting] = useState(false);
    const [currentBagTaskLogId, setCurrentBagTaskLogId] = useState<number | undefined>();

    // 消息国际化转换函数
    const translateBackendMessage = (message: string): string => {
        switch (message) {
            case 'taskInProgress':
                return t('wms.baggingTask.messages.taskInProgress');
            default:
                return message; // 如果没有匹配的翻译，返回原消息
        }
    };

    const [executeConsolidationAsyncMutation, { loading: mutationLoading }] = useMutation<
        ExecuteConsolidationAsyncResponse,
        ExecuteConsolidationAsyncVariables
    >(EXECUTE_CONSOLIDATION_ASYNC, {
        onCompleted: (data) => {
            if (data.executeConsolidationAsync.success) {
                const bagTaskLogId = parseInt(data.executeConsolidationAsync.bagTaskLogId);
                setCurrentBagTaskLogId(bagTaskLogId);
                // 开始轮询将在useBagTaskLogPolling中自动处理
            } else {
                setIsExecuting(false);
                const errorMsg = translateBackendMessage(data.executeConsolidationAsync.message || "");
                onError?.(errorMsg);
            }
        },
        onError: (error) => {
            setIsExecuting(false);
            const errorMsg = `${t('wms.baggingTask.messages.bagTaskStartFailed') || 'The bag task has failed to start.'}: ${error.message}`;
            message.error(errorMsg);
            onError?.(errorMsg);
        },
        refetchQueries: [GET_BAG_TASK_LOG] // 确保轮询查询能获取最新数据
    });

    const {
        data: taskLogData,
        loading: pollingLoading,
        error: pollingError,
        isPolling
    } = useBagTaskLogPolling({
        bagTaskLogId: currentBagTaskLogId,
        pollingInterval,
        maxPollingTime,
        onSuccess: (data) => {
            setIsExecuting(false);
            setCurrentBagTaskLogId(undefined);
            onSuccess?.(data);
        },
        onError: (error) => {
            setIsExecuting(false);
            setCurrentBagTaskLogId(undefined);
            onError?.(error);
        }
    });

    const executeAsync = async (variables: ExecuteConsolidationAsyncVariables) => {
        try {
            setIsExecuting(true);
            await executeConsolidationAsyncMutation({ variables });
        } catch (error) {
            setIsExecuting(false);
            // 错误已在onError中处理
        }
    };

    return {
        executeAsync,
        loading: mutationLoading,
        isExecuting: isExecuting || isPolling,
        currentTaskLog: taskLogData,
        error: pollingError
    };
};
